// src/pages/AuthorWorksPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Loader2, BookOpen, Languages, FileText, ArrowLeft } from 'lucide-react';
import { FaRegPenToSquare } from "react-icons/fa6";
import { RiExchangeBoxLine } from "react-icons/ri";

import apiClient from '../api/apiClient';
import BookCover from '../components/library/BookCover';
import ContentCard from '../components/Product/ContentCard';

const AuthorWorksPage = () => {
    const [latestBooks, setLatestBooks] = useState([]);
    const [booksLoading, setBooksLoading] = useState(true);
    const [recommendedProducts, setRecommendedProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(true);

    // دریافت کتاب‌های دارای تگ mostafa-malekian (قدیمی‌ترین اول)
    useEffect(() => {
        const fetchLatestBooks = async () => {
            setBooksLoading(true);
            try {
                const response = await apiClient.get('/books/', {
                    params: {
                        tags__slug: 'mostafa-malekian',
                        ordering: 'created_at',            // ← صعودی (قدیمی‌ترین اول)
                        page_size: 20
                    }
                });
                const books = response.data.results || response.data || [];
                const unique = new Map();
                books.forEach(book => {
                    if (!unique.has(book.slug)) unique.set(book.slug, book);
                });
                // مرتب‌سازی قدیمی‌ترین به جدیدترین
                const sorted = Array.from(unique.values()).sort(
                    (a, b) => new Date(a.created_at) - new Date(b.created_at)
                );
                setLatestBooks(sorted.slice(0, 9));
            } catch (err) {
                console.error('Error fetching latest books:', err);
            } finally {
                setBooksLoading(false);
            }
        };
        fetchLatestBooks();
    }, []);

    // بخش پیشنهادات (بدون تغییر)
    useEffect(() => {
        const fetchRecommendedProducts = async () => {
            setProductsLoading(true);
            try {
                const response = await apiClient.get('/products/list/', {
                    params: { tags__slug: 'پیشنهادات-فرزانه', ordering: '-created_at', page_size: 15 }
                });
                const products = response.data.results || response.data || [];
                setRecommendedProducts(products);
            } catch (err) {
                console.error('Error fetching recommended products:', err);
            } finally {
                setProductsLoading(false);
            }
        };
        fetchRecommendedProducts();
    }, []);

    const getTagLink = (tagSlug) => `/books?tags__slug=${tagSlug}`;

    const icons = {
        تالیف: <BookOpen size={17} className="text-purple-700 mb-2" />,
        ترجمه: <RiExchangeBoxLine size={17} className="text-purple-700 mb-2" />,
        مقدمه: <FileText size={17} className="text-purple-700 mb-2" />,
        ویراسته: <FaRegPenToSquare size={17} className="text-purple-700 mb-2" />,
    };

    const defaultOgImage = 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/og-image.jpg';

    // تقسیم کتاب‌ها به دو بخش: ردیف اول (۳ کتاب) و بقیه
    const firstRowBooks = latestBooks.slice(0, 3);
    const remainingBooks = latestBooks.slice(3);

    return (
        <>
            <Helmet>
                <title>آثار مصطفی ملکیان | فرزانه</title>
                <meta name="description" content="مجموعه‌ای از تألیفات، ترجمه‌ها، مقدمه‌ها و ویراسته‌های مصطفی ملکیان، فیلسوف و اندیشمند معاصر. جدیدترین آثار منتشر شده از استاد مصطفی ملکیان." />
                <meta property="og:title" content="آثار مصطفی ملکیان | فرزانه" />
                <meta property="og:description" content="تمامی آثار مصطفی ملکیان شامل کتاب‌ها، ترجمه‌ها، مقدمه‌ها و ویراسته‌ها در یکجا. با قابلیت مطالعه و خرید." />
                <meta property="og:image" content={defaultOgImage} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="آثار مصطفی ملکیان | فرزانه" />
                <meta name="twitter:description" content="مجموعه کامل آثار مصطفی ملکیان؛ فیلسوف اخلاق و معنویت." />
                <meta name="twitter:image" content={defaultOgImage} />
            </Helmet>

            <div className="min-h-screen bg-white rtl pb-20">
                {/* هدر با تصویر پس‌زمینه */}
                <div 
                    className="p-6 pt-12 text-white rounded-b-3xl shadow-xl bg-cover bg-center bg-no-repeat"
                    style={{ 
                        backgroundImage: `url('https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/Home-2.jpg')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                    }}
                >
                    <div className="flex justify-between items-start mb-4 p-20">
                        <div className="opacity-0"></div>
                        <div className="opacity-70"></div>
                    </div>
                </div>

                {/* چهار کارت دسته‌بندی */}
                <div className="p-4 -mt-10 relative z-5 mb-2">
                    <div className="grid grid-cols-4 gap-1">
                        <Link to={getTagLink('Author')} className="bg-white p-4 rounded-xl shadow-lg text-center rtl flex flex-col items-center justify-center cursor-pointer hover:shadow-xl transition-shadow">
                            {icons['تالیف']}
                            <span className="text-[13px] font-reqular text-gray-800">تـالـیفات</span>
                        </Link>
                        <Link to={getTagLink('Translation')} className="bg-white p-4 rounded-xl shadow-lg text-center rtl flex flex-col items-center justify-center cursor-pointer hover:shadow-xl transition-shadow">
                            {icons['ترجمه']}
                            <span className="text-[13px] font-regular text-gray-800">تـرجـمه‌ها</span>
                        </Link>
                        <Link to={getTagLink('Introduction')} className="bg-white p-4 rounded-xl shadow-lg text-center rtl flex flex-col items-center justify-center cursor-pointer hover:shadow-xl transition-shadow">
                            {icons['مقدمه']}
                            <span className="text-[13px] font-regular text-gray-800">مقـدمه‌ها</span>
                        </Link>
                        <Link to={getTagLink('Editor')} className="bg-white p-4 rounded-xl shadow-lg text-center rtl flex flex-col items-center justify-center cursor-pointer hover:shadow-xl transition-shadow">
                            {icons['ویراسته']}
                            <span className="text-[13px] font-regular text-gray-800">ویـراستـه‌ها</span>
                        </Link>
                    </div>
                </div>

                {/* بخش جدیدترین آثار با دکمه بیشتر */}
                <div className="container mx-auto px-4 max-w-7xl ">
                    <div className="mb-2 p-4 bg-white border border-gray-200 rounded-xl shadow-s rtl text-right">
                        <div className="flex justify-between items-center mb-6 pr-3">
                            <h2 className="text-[14px] font-medium text-gray-600">
                               جدیـدتـرین آثار منتشر شده مصطفی ملکیان
                            </h2>
                            <Link
                                to="/books?tags__slug=mostafa-malekian"
                                className="text-xs text-gray-600 hover:text-purple-700 flex items-center gap-1 transition-colors"
                            >
                                <span>بیشتر</span>
                                <ArrowLeft size={14} />
                            </Link>
                        </div>
                        {booksLoading ? (
                            <div className="flex justify-center py-2">
                                <Loader2 className="animate-spin text-gray-800" size={5} />
                            </div>
                        ) : (
                            <>
                                {/* ردیف اول: ۳ کتاب اول */}
                                <div className="grid grid-cols-3 gap-6">
                                    {firstRowBooks.map(book => (
                                        <BookCover key={book.slug} book={book} />
                                    ))}
                                </div>

                                {/* بنر اختصاصی بین ردیف اول و دوم */}
                                <div className="my-6">
                                    <a
                                        href="https://farzaneh.app/books/on-dialogue"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <img
                                            src="https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/3-1.png"
                                            alt="بنر کتاب"
                                            className="w-full rounded-xl object-cover shadow-md hover:shadow-lg transition-shadow"
                                            style={{ maxHeight: '200px' }}
                                        />
                                    </a>
                                </div>

                                {/* ردیف‌های بعدی: بقیهٔ کتاب‌ها (۶ کتاب باقی‌مانده) */}
                                <div className="grid grid-cols-3 gap-6">
                                    {remainingBooks.map(book => (
                                        <BookCover key={book.slug} book={book} />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* بخش پیشنهادات فرزانه */}
                    <div>
                        <h2 className="text-[14px] font-medium text-gray-600 mb-6 pr-3 mt-6">
                            پـیـشـنـهادهای فـرزانه
                        </h2>
                        {productsLoading ? (
                            <div className="flex justify-center py-12">
                                <Loader2 className="animate-spin text-indigo-600" size={15} />
                            </div>
                        ) : (
                            <div className="grid grid-cols-3 gap-6">
                                {recommendedProducts.map(product => (
                                    <ContentCard key={product.id} data={product} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default AuthorWorksPage;